// Fallback shown when a job errored but the backend didn't provide a reason.
export const DEFAULT_JOB_ERROR_MESSAGE =
  "The calculation failed unexpectedly. Please try running it again. If it keeps failing, contact support with this job so we can investigate.";

// Keep the tooltip readable — very long reasons get truncated with an ellipsis.
const MAX_ERROR_MESSAGE_LENGTH = 300;

/**
 * Truncates a message to `maxLength` characters, appending an ellipsis when
 * truncated. Unlike getJobErrorTooltip, this has NO generic fallback — pass
 * through as-is (or truncated) with no substitution for an empty message.
 */
export function truncateMessage(
  message: string,
  maxLength: number = MAX_ERROR_MESSAGE_LENGTH
): string {
  if (message.length <= maxLength) {
    return message;
  }

  return `${message.slice(0, maxLength)}…`;
}

/**
 * Builds the reason text shown for a failed job (tooltip / modal / recalculate
 * confirm). The reason is sanitized UPSTREAM by the backend — DescribeFailure
 * maps exceptions to safe, human-readable messages and never echoes raw
 * exception text (OWASP A10) — so this helper does not re-sanitize; it only
 * trims, truncates long reasons for readability, and falls back to a generic
 * message when `errorMessage` is null/empty.
 *
 * `fallback` lets a caller substitute a reason that fits its own case — the "No results" pill
 * passes one, since a generic "calculation failed" would contradict the pill it sits on.
 */
export function getJobErrorTooltip(
  errorMessage?: Maybe<string>,
  fallback: string = DEFAULT_JOB_ERROR_MESSAGE
): string {
  const message = errorMessage?.trim() || fallback;

  return truncateMessage(message, MAX_ERROR_MESSAGE_LENGTH);
}
